import type { WeaponDefinition, WeaponContext, WeaponResult } from './types';
import { Entity, generateEntityId } from '../entities/entity';
import { WA_PHYSICS, type Vector2 } from '../core/constants';
import { computeSafeSpawnPosition } from './spawnHelper';

const RAON = {
  COUNT: 2,
  /** Walk speed (px/s) - a slow, deliberate stalk. */
  WALK_SPEED: 55,
  EXPLOSION_RADIUS: 40,
  MAX_DAMAGE: 35,
  /** Max horizontal distance the bot walks each turn (the budget resets at
   *  the start of every turn, so it keeps advancing turn after turn). */
  MOVEMENT_PER_TURN: 90,
  /** Turns the bot survives before self-destructing if it never connects. */
  MAX_TURNS: 4,
  /** Launch fan so the two bots land apart. */
  SPREAD_STEP_DEG: 6,
  /** Enemy must land within this range for the bot to wake and hunt; a shot
   *  that drops it farther off (an imprecise shot) leaves it idle until an
   *  enemy comes near. ~1.3 turns of walking, so the landing has to be close. */
  AWAKE_RADIUS: 120,
  WIDTH: 16,
  HEIGHT: 14,
  MAX_CLIMB: 6,
} as const;

interface SeekWorm {
  position: Vector2;
  teamId: string;
  state: string;
}

/**
 * MiniRaon (GunBound Raon Launcher, Shot 2): a little penguin bot. Thrown in
 * an arc; once it lands it walks along the terrain toward the nearest worm of
 * ANY team (its antenna lights up when it has a target) and detonates on
 * contact - it does not spare allies (friendly fire is intentional), so keep
 * your own worms clear. Self-destructs when its fuse runs out. Raon Shot 2
 * fires two of them.
 */
export class MiniRaon implements Entity {
  /** Discriminator so the weapon executor routes it correctly. */
  readonly isRaon = true;

  id: string;
  position: Vector2;
  velocity: Vector2 = { x: 0, y: 0 };
  active = true;
  isGrounded = false;

  facing: 'left' | 'right';
  armed = false;
  walkSpeed = RAON.WALK_SPEED;
  explosionRadius = RAON.EXPLOSION_RADIUS;
  maxDamage = RAON.MAX_DAMAGE;
  ownerId: string;
  ownerTeamId: string;

  /** Horizontal distance still allowed this turn; refreshed each turn. */
  private movementRemaining = RAON.MOVEMENT_PER_TURN;
  private lastTurnSeq = -1;
  private turnsAlive = 0;

  onExplode?: (x: number, y: number) => void;
  onArmed?: () => void;

  constructor(
    x: number,
    y: number,
    facing: 'left' | 'right',
    ownerId: string,
    ownerTeamId: string,
    launchVx: number,
    launchVy: number,
  ) {
    this.id = generateEntityId('raon');
    this.position = { x, y };
    this.facing = facing;
    this.ownerId = ownerId;
    this.ownerTeamId = ownerTeamId;
    this.velocity.x = launchVx;
    this.velocity.y = launchVy;
  }

  /** Nearest live worm of ANY team - the bot triggers on friendly worms too
   *  (intentional friendly fire), so allies should clear out of its path. */
  private nearestWorm(worms: SeekWorm[]): SeekWorm | null {
    let best: SeekWorm | null = null;
    let bestDist = Infinity;
    for (const w of worms) {
      if (w.state === 'dead') continue;
      const dx = w.position.x - this.position.x;
      const dy = w.position.y - this.position.y;
      const d = dx * dx + dy * dy;
      if (d < bestDist) {
        bestDist = d;
        best = w;
      }
    }
    return best;
  }

  update(
    deltaTime: number,
    terrain?: { isSolid(x: number, y: number): boolean },
    worms: SeekWorm[] = [],
    turnSequence?: number,
    mayWalk = true,
  ): void {
    if (!this.active) return;

    // A new turn refreshes the movement budget (so the bot advances every
    // turn); after a few turns without connecting it self-destructs.
    if (turnSequence !== undefined && turnSequence !== this.lastTurnSeq) {
      this.lastTurnSeq = turnSequence;
      this.movementRemaining = RAON.MOVEMENT_PER_TURN;
      this.turnsAlive++;
      if (this.turnsAlive > RAON.MAX_TURNS) {
        this.explode();
        return;
      }
    }

    if (!this.isGrounded || !terrain) {
      // Airborne (or no terrain context): arc through the air until it lands.
      this.velocity.y += WA_PHYSICS.GRAVITY * deltaTime;
      this.position.x += this.velocity.x * deltaTime;
      this.position.y += this.velocity.y * deltaTime;
      return;
    }

    // Landed. Detonate immediately on contact with any worm's hitbox (friend
    // or foe - the bot does not discriminate).
    if (this.collidingWorm(worms)) {
      this.explode();
      return;
    }

    // Proximity drives the antenna light (green = locked on, red = idle) and
    // is evaluated every tick - even during a player's turn - so the indicator
    // is always live. ANY worm within range wakes the bot (including allies);
    // otherwise it idles (and resumes the moment any worm comes near).
    const target = this.nearestWorm(worms);
    const inRange =
      target !== null && this.dist2ToCenter(target) <= RAON.AWAKE_RADIUS * RAON.AWAKE_RADIUS;
    if (!target || !inRange) {
      this.armed = false;
      this.velocity.x = 0;
      return;
    }

    if (!this.armed) {
      this.armed = true;
      this.onArmed?.();
    }
    this.facing = target.position.x < this.position.x ? 'left' : 'right';

    // The bot only walks while the turn is resolving / handing over (right
    // after it lands and between turns) - never while a player is actively in
    // control of their own worm. Off-turn it stays put but keeps its antenna
    // lit toward the target.
    if (!mayWalk) {
      this.velocity.x = 0;
      return;
    }

    // Walk toward the target, but only while this turn's budget lasts.
    if (this.movementRemaining > 0) {
      this.velocity.x = this.facing === 'right' ? this.walkSpeed : -this.walkSpeed;
      const moved = this.walkAlongTerrain(deltaTime, terrain);
      this.movementRemaining -= Math.abs(moved);
    } else {
      this.velocity.x = 0; // budget spent; wait for the next turn
    }
  }

  /** Squared distance from a worm's centre to the bot's centre. */
  private dist2ToCenter(w: SeekWorm): number {
    const cx = this.position.x + RAON.WIDTH / 2;
    const cy = this.position.y + RAON.HEIGHT / 2;
    const wx = w.position.x + WA_PHYSICS.WORM.WIDTH / 2;
    const wy = w.position.y + WA_PHYSICS.WORM.HEIGHT / 2;
    const dx = wx - cx;
    const dy = wy - cy;
    return dx * dx + dy * dy;
  }

  /** First live worm (any team) whose hitbox overlaps the bot's body, or null. */
  private collidingWorm(worms: SeekWorm[]): SeekWorm | null {
    const bx = this.position.x;
    const by = this.position.y;
    for (const w of worms) {
      if (w.state === 'dead') continue;
      const overlapX =
        Math.min(bx + RAON.WIDTH, w.position.x + WA_PHYSICS.WORM.WIDTH) -
        Math.max(bx, w.position.x);
      const overlapY =
        Math.min(by + RAON.HEIGHT, w.position.y + WA_PHYSICS.WORM.HEIGHT) -
        Math.max(by, w.position.y);
      if (overlapX > 0 && overlapY > 0) return w;
    }
    return null;
  }

  /**
   * Slope-following ground walk (shared shape with the Sheep walker). Caps
   * the step to the remaining per-turn budget and returns the distance moved.
   */
  private walkAlongTerrain(
    deltaTime: number,
    terrain: { isSolid(x: number, y: number): boolean },
  ): number {
    const { WIDTH: width, HEIGHT: height, MAX_CLIMB: maxClimb } = RAON;
    let moveX = this.velocity.x * deltaTime;
    // Don't walk further than this turn's remaining budget allows.
    if (Math.abs(moveX) > this.movementRemaining) {
      moveX = Math.sign(moveX) * this.movementRemaining;
    }
    if (moveX === 0) return 0;

    const nextX = this.position.x + moveX;
    const edgeX = moveX > 0 ? nextX + width : nextX;

    for (let climbY = 0; climbY <= maxClimb; climbY++) {
      const testY = this.position.y - climbY;
      const blocked =
        terrain.isSolid(edgeX, testY + 2) || terrain.isSolid(edgeX, testY + height / 2);
      if (!blocked) {
        this.position.x = nextX;
        this.position.y = testY;
        for (let dropY = 0; dropY <= maxClimb + 2; dropY++) {
          if (terrain.isSolid(this.position.x + width / 2, this.position.y + height + dropY)) {
            this.position.y += dropY;
            break;
          }
        }
        return moveX;
      }
    }
    // Wall it can't climb: stop for now (it may try again next turn).
    this.velocity.x = 0;
    return 0;
  }

  applyTerrainPhysics(terrain: { isSolid(x: number, y: number): boolean }): void {
    if (!this.active) return;

    const { WIDTH: width, HEIGHT: height } = RAON;
    const feetY = this.position.y + height;

    if (this.velocity.y >= 0) {
      for (let dy = 0; dy <= 6; dy++) {
        if (terrain.isSolid(this.position.x + width / 2, feetY + dy)) {
          this.position.y = feetY + dy - height;
          this.velocity.y = 0;
          this.isGrounded = true;
          return;
        }
      }
    }

    if (this.isGrounded) {
      const hasGround =
        terrain.isSolid(this.position.x + 2, feetY + 1) ||
        terrain.isSolid(this.position.x + width / 2, feetY + 1) ||
        terrain.isSolid(this.position.x + width - 2, feetY + 1);
      if (!hasGround) {
        this.isGrounded = false;
      }
    }
  }

  applyExplosionForce(pushX: number, pushY: number): void {
    this.velocity.x += pushX;
    this.velocity.y += pushY;
    this.isGrounded = false;
  }

  checkWaterCollision(waterLevel: number, worldHeight: number): boolean {
    return this.position.y > worldHeight - waterLevel;
  }

  explode(): void {
    if (!this.active) return;
    this.active = false;
    this.onExplode?.(this.position.x + RAON.WIDTH / 2, this.position.y + RAON.HEIGHT / 2);
  }

  destroy(): void {
    this.active = false;
  }
}

export const WeaponRaon: WeaponDefinition = {
  id: 'raon',
  name: 'Raon',
  icon: 'raon.png',
  category: 'thrown',
  maxAmmo: 2,
  maxUsesPerTurn: 1,
  powerCharging: true,
  timerAdjustable: false,
  showAimGuide: true,
  allowRetreat: true,

  fire(ctx: WeaponContext): WeaponResult {
    const { worm, angle, power, world } = ctx;

    const speed = (power / 100) * 750;
    const center = (RAON.COUNT - 1) / 2;

    const bots: MiniRaon[] = [];
    for (let i = 0; i < RAON.COUNT; i++) {
      const offsetDeg = (i - center) * RAON.SPREAD_STEP_DEG;
      const rad = ((angle + offsetDeg) * Math.PI) / 180;
      const aimRad = worm.facing === 'right' ? rad : Math.PI - rad;
      const spawn = computeSafeSpawnPosition(worm, aimRad, 20, world);
      bots.push(
        new MiniRaon(
          spawn.x,
          spawn.y,
          worm.facing,
          worm.id,
          worm.teamId,
          Math.cos(aimRad) * speed,
          -Math.sin(aimRad) * speed,
        ),
      );
    }

    return {
      success: true,
      entities: bots,
      message: 'Deploy!',
    };
  },
};
